import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { axiosInstance, localAxiosGet } from '../../../Axios/axios';
import {
    Typography, Box, TableContainer, Table, TableCell, TableRow, Chip, Divider,
    Avatar, Button, Modal, Accordion, AccordionSummary, AccordionDetails, TableBody
} from '@mui/material';
import {
    Business, AccountCircle, People, Subject, PriorityHigh, CalendarToday,
    PlaylistAddCheck, ExpandMore
} from '@mui/icons-material'
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';

const formatDate = (dateString) => {
    if (!dateString) return 'Not Set';
    const date = new Date(dateString);
    return date.toLocaleDateString();
};

const ProjectDetails = ({ open, selectedProjectId, onClose }) => {
    const [projectDetails, setProjectDetails] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const accessToken = localStorage.getItem('accessToken');

    useEffect(() => {
        const fetchProjectDetails = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await axiosInstance(localAxiosGet(`/project/getProjectById/${selectedProjectId}`, accessToken))
                if (response.data.status === 'SUCCESS') {
                    setProjectDetails(response.data.message.data);
                } else {
                    setError(response.data.message || 'Failed to Fetch Project Details')
                }
            } catch (error) {
                console.log("Error in fetching Project details", error)
            } finally {
                setIsLoading(false)
            }
        }
        if (selectedProjectId) {
            fetchProjectDetails();
        }
    }, [selectedProjectId]);

    if (!selectedProjectId) {
        return null;
    }
    if (!projectDetails) {
        return <Typography>No project details available.</Typography>;
    }

    const { project, manager, team_leader, departments, clients } = projectDetails;

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="project-details-modal"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '95%',
                    maxWidth: '900px',
                    height: '90vh',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                    boxShadow: 24,
                    p: 3,
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {/* Main Content Container */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        flexDirection: { xs: 'column', md: 'row' },
                        flex: 1, // Take available space
                        overflow: 'hidden',
                    }}
                >

                    {/* Left Section: Project Details */}
                    <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            p: 1.5,
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            borderRadius: '8px',
                            mb: 2,
                            flexShrink: 0 // Prevent shrinking
                        }}>
                            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                <Business sx={{ mr: 1, fontSize: '20px' }} />
                                Project Details
                            </Typography>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            overflow: 'auto', // Enable scrolling for content
                            pr: 1 // Add some padding for scrollbar
                        }}>

                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {[
                                            {
                                                icon: <Subject sx={{ color: '#64b5f6', fontSize: '18px' }} />,
                                                label: 'Project Name',
                                                value: project?.project_title || 'Not Available',
                                                component: <Typography variant="body2" color="text.secondary">{project?.project_title || 'Not Available'}</Typography>
                                            },
                                            {
                                                icon: <PriorityHigh sx={{ color: '#f44336', fontSize: '18px' }} />,
                                                label: 'Priority',
                                                value: project?.priority,
                                                component: (
                                                    <Chip
                                                        label={project?.priority || 'Not Set'}
                                                        color={
                                                            project?.priority === 'High' ? 'error' :
                                                                project?.priority === 'Medium' ? 'warning' :
                                                                    project?.priority === 'Low' ? 'success' : 'default'
                                                        }
                                                        size="small"
                                                    />
                                                )
                                            },
                                            {
                                                icon: <CalendarToday sx={{ color: '#00bcd4', fontSize: '18px' }} />,
                                                label: 'Start Date',
                                                value: formatDate(project?.start_date),
                                                component: <Typography variant="body2">{formatDate(project?.start_date)}</Typography>
                                            },
                                            {
                                                icon: <CalendarToday sx={{ color: '#00bcd4', fontSize: '18px' }} />,
                                                label: 'End Date',
                                                value: formatDate(project?.end_date),
                                                component: <Typography variant="body2">{formatDate(project?.end_date)}</Typography>
                                            },
                                            {
                                                icon: <PlaylistAddCheck sx={{ color: '#4caf50', fontSize: '18px' }} />,
                                                label: 'Status',
                                                value: project?.status,
                                                component: (
                                                    <Chip
                                                        label={project?.status || 'Not Set'}
                                                        color={
                                                            project?.status === 'Completed' ? 'success' :
                                                                project?.status === 'In Progress' ? 'warning' :
                                                                    project?.status === 'Todo' ? 'primary' : 'default'
                                                        }
                                                        size="small"
                                                    />
                                                )
                                            },
                                            {
                                                icon: <SubjectIcon sx={{ color: '#757575', fontSize: '18px' }} />,
                                                label: 'Description',
                                                value: project?.description,
                                                component: (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {project?.description || 'No description available'}
                                                    </Typography>
                                                )
                                            },
                                        ].map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ border: 'none', py: 1, width: '35%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {item.icon}
                                                        <Typography variant="body2" fontWeight="500">
                                                            {item.label}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    {item.component}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* Clients */}
                                        {clients && clients.length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PersonIcon sx={{ color: '#1976d2', fontSize: '18px' }} />
                                                        <Typography variant="body2" fontWeight="500">Clients</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {clients.map(client => (
                                                            <Chip
                                                                key={client.id}
                                                                label={`${client.first_name} ${client.last_name}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                    {/* Right Section: Team Details */}
                    <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            p: 1.5,
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            borderRadius: '8px',
                            mb: 2,
                            flexShrink: 0 // Prevent shrinking
                        }}>
                            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                <People sx={{ mr: 1, fontSize: '20px' }} />
                                Team Information
                            </Typography>
                        </Box>

                        <Box sx={{
                            flex: 1,
                            overflow: 'auto', // Enable scrolling for content
                            pr: 1 // Add some padding for scrollbar
                        }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        {manager && (
                                            <TableRow>
                                                <TableCell sx={{ border: 'none', py: 1, width: '35%' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccountCircle sx={{ color: '#7c4dff', fontSize: '18px' }} />
                                                        <Typography variant="body2" fontWeight="500">Manager</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    <Chip
                                                        label={`${manager.first_name} ${manager.last_name}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {team_leader && (
                                            <TableRow>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccountCircle sx={{ color: '#2e7d32', fontSize: '18px' }} />
                                                        <Typography variant="body2" fontWeight="500">Team Leader</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ border: 'none', py: 1 }}>
                                                    <Chip
                                                        label={`${team_leader.first_name} ${team_leader.last_name}`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* Departments with Teams and Members */}
                                        {departments && departments.length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ border: 'none', py: 1 }} colSpan={2}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <People sx={{ color: '#009688', fontSize: '18px' }} />
                                                            <Typography variant="body2" fontWeight="500">
                                                                Departments & Teams
                                                            </Typography>
                                                        </Box>

                                                        {/* Departments, Teams and Members Display */}
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            {departments.map((department) => (
                                                                <Box key={department.department_id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                                    {/* Department Header */}
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                                                                        <Business sx={{ color: '#10b981', fontSize: '20px' }} />
                                                                        <Box>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: '500' }}>
                                                                                Department
                                                                            </Typography>
                                                                            <Typography variant="body2" fontWeight="600" color="#10b981">
                                                                                {department.department_name}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>

                                                                    {/* Teams and Members */}
                                                                    {department.teams && department.teams.length > 0 ? (
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 3 }}>
                                                                            {department.teams.map((team) => (
                                                                                <Box key={team.team_id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 1.5 }}>
                                                                                    <People sx={{ color: '#6366f1', fontSize: '18px', mt: 0.5 }} />
                                                                                    <Box sx={{ flex: 1 }}>
                                                                                        <Typography variant="body2" fontWeight="500" color="#6366f1" sx={{ mb: 1 }}>
                                                                                            {team.team_name}
                                                                                        </Typography>
                                                                                        {team.members && team.members.length > 0 ? (
                                                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                                {team.members.map((member) => (
                                                                                                    <Chip
                                                                                                        key={member.id}
                                                                                                        label={`${member.first_name} ${member.last_name}`}
                                                                                                        size="small"
                                                                                                        sx={{
                                                                                                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                                                                            color: '#6366f1',
                                                                                                            fontSize: '11px',
                                                                                                            height: '24px'
                                                                                                        }}
                                                                                                    />
                                                                                                ))}
                                                                                            </Box>
                                                                                        ) : (
                                                                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                                                                No members assigned
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ pl: 3, p: 1.5 }}>
                                                                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                                                                No teams in this department
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                </Box>

                {/* Close Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        variant="contained"
                        onClick={onClose}
                        color="primary"
                        size="small"
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ProjectDetails;